import { FC } from "react";

import { ToolUIPart, UIMessage } from "ai";

import { ConnectedApprovalCard } from "./ApprovalCard/ConnectedApprovalCard";
import { getToolPartView } from "./helpers";

import { AssistantMessageSource, useAssistantConfig } from "../../contexts/AssistantConfigContext";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

interface ToolPartProps {
  part: ToolUIPart;
  reply?: UIMessage;
  messages: UIMessage[];
  isComposerDisabled: boolean;
  onAnswerApproval: (approvalId: string, approved: boolean, note?: string) => void;
  onMessageSent: (source: AssistantMessageSource) => void;
}

export const ToolPart: FC<ToolPartProps> = ({
  part,
  reply,
  messages,
  isComposerDisabled,
  onAnswerApproval,
  onMessageSent,
}) => {
  const { t } = useTranslate();
  const config = useAssistantConfig();
  const Renderer = config.toolRenderers[part.type];
  const view = getToolPartView(part, config.getToolPartDisplay(part), Boolean(Renderer));

  if (view === "approval" && part.state === "approval-requested") {
    return <ConnectedApprovalCard part={part} messages={messages} onAnswerApproval={onAnswerApproval} />;
  }
  if (view === "output" && Renderer) {
    return <Renderer part={part} reply={reply} isComposerDisabled={isComposerDisabled} onMessageSent={onMessageSent} />;
  }
  if (view === "uncertain") {
    return (
      <DashboardTypography role="status" variant="body2">
        {t("dashboard", "assistant.recovery.uncertain")}
      </DashboardTypography>
    );
  }
  if (view === "error" && part.state === "output-available") {
    return <DashboardTypography variant="body2">{t("dashboard", "assistant.tool.error")}</DashboardTypography>;
  }
  if (view === "error" || view === "confirmed" || view === "declined") {
    const captions = {
      error: t("dashboard", "assistant.tool.error"),
      confirmed: t("dashboard", "assistant.approval.confirmed"),
      declined: t("dashboard", "assistant.approval.declined"),
    };
    return (
      <DashboardTypography variant="caption" vibe="text-neutral-moderate">
        {captions[view]}
      </DashboardTypography>
    );
  }
  return null;
};
